import React from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const Features = () => {
  const FeatureSection = ({ title, desc, icon, items, image, reverse }) => (
    <div className={`flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-12 py-20`}>
      <div className="flex-1 space-y-6">
        <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 text-3xl mb-4 shadow-sm border border-blue-100">
          <SafeIcon icon={icon} />
        </div>
        <h2 className="text-4xl font-black text-slate-900 tracking-tight">{title}</h2>
        <p className="text-lg text-slate-600 leading-relaxed font-medium">{desc}</p>
        <ul className="space-y-4 pt-4">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <SafeIcon icon="✅" className="text-emerald-500 mt-1 flex-shrink-0" />
              <span className="text-slate-700 font-bold">{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex-1 w-full">
        <div className="bg-slate-100 rounded-[2.5rem] p-8 border border-slate-200 shadow-xl rotate-1 hover:rotate-0 transition-transform duration-500">
          <img src={image} alt={title} className="rounded-xl shadow-sm w-full h-auto" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      {/* Nav */}
      <nav className="fixed w-full z-50 bg-white/90 backdrop-blur-md border-b border-slate-100 py-4">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-black">S</div>
            <span className="font-black text-lg">SalesWork CRM</span>
          </Link>
          <div className="flex gap-4">
            <Link to="/login" className="px-6 py-2 bg-slate-900 text-white rounded-lg font-bold text-sm">Prøv Gratis</Link>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-24">
          <h1 className="text-5xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight">
            En komplett verktøykasse for <span className="text-blue-600">moderne salg</span>
          </h1>
          <p className="text-xl text-slate-500 font-medium">
            Fra prospektering til signering. Vi har samlet alt du trenger i én plattform.
          </p>
        </div>

        <FeatureSection 
          title="Lead Intelligence & Brreg" 
          desc="Aldri tast inn firmadata manuelt igjen. Vår motor henter data direkte fra Brønnøysundregistrene og beriker dem med sanntids-signaler." 
          icon="🏢" 
          items={[
            "Søk i alle norske bedrifter",
            "Importer styre, ledelse og regnskapstall",
            "Få varsler ved endringer (Lead Signals)",
            "Automatisk vask av lister"
          ]} 
          image="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80" 
        />

        <FeatureSection 
          reverse 
          title="LinkedIn Outreach (Safe Mode)" 
          desc="Prospektering på LinkedIn gjort riktig. Vi hjelper deg å strukturere arbeidet uten å risikere kontoen din." 
          icon="👔" 
          items={[
            "100% trygg (Ingen ulovlig scraping)",
            "AI-genererte isbrytere og meldinger",
            "Strukturert oppgaveliste for manuell sending",
            "Sporing av respons"
          ]} 
          image="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&q=80" 
        />

        <FeatureSection 
          title="AI Salgscoach & Copilot" 
          desc="Din personlige assistent som analyserer deals og hjelper deg å skrive bedre e-poster." 
          icon="🤖" 
          items={[
            "Sanntids analyse av pipeline",
            "Forslag til neste steg (Next Best Action)",
            "Generering av e-postutkast basert på kontekst",
            "Spar timer på administrasjon"
          ]} 
          image="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&q=80" 
        />

        <FeatureSection 
          reverse 
          title="Avansert Rapportering & Dashboard" 
          desc="Få full kontroll over salgsprestasjonen med sanntidsdata og visuelle grafer." 
          icon="📊" 
          items={[
            "Følg med på Win Rate og konvertering",
            "Omsetning per selger eller avdeling",
            "Eksportér rapporter til PDF for ledermøter",
            "Aktivitetssporing for hele teamet"
          ]} 
          image="https://images.unsplash.com/photo-1551288049-bbda38a10ad5?w=800&q=80" 
        />

        <FeatureSection 
          title="E-post Sekvenser & Automasjon" 
          desc="Sett oppfølgingen i system. Bygg drypp-kampanjer som sikrer at ingen leads faller gjennom sprekkene." 
          icon="📧" 
          items={[
            "Bygg sekvenser med e-post, pauser og oppgaver",
            "A/B-testing av emnefelt og innhold",
            "Sporing av åpningsrate og lenkeklikk",
            "Importer ferdige maler fra CSV"
          ]} 
          image="https://images.unsplash.com/photo-1557200134-90327ee9fafa?w=800&q=80" 
        />

        <FeatureSection 
          reverse 
          title="Playbook & Battlecards" 
          desc="Gi teamet ditt ammunisjonen de trenger for å vinne. Standardiser beste praksis for hele salgsavdelingen." 
          icon="📖" 
          items={[
            "Opprett salgsscenarioer og sjekklister",
            "Battlecards mot spesifikke konkurrenter",
            "Gjør playbooks om til treningsmoduler (E-læring)",
            "Del beste praksis på tvers av avdelinger"
          ]} 
          image="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80" 
        />

        <FeatureSection 
          title="E-signering & Kunderom" 
          desc="Lukk avtalen raskere med integrert signering og sikre kunderom for deling av dokumenter." 
          icon="✍️" 
          items={[
            "Juridisk bindende e-signatur (BankID klare)",
            "Sporing av når kunden åpner tilbudet",
            "Sikker deling av filer i dedikert Kunderom",
            "Ferdige maler for kontrakter"
          ]} 
          image="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80" 
        />

        {/* CTA */}
        <div className="mt-20 text-center bg-slate-900 rounded-[3rem] p-16 text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
          <div className="relative z-10">
            <h2 className="text-4xl font-black mb-6">Klar for å oppgradere salgsprosessen?</h2>
            <Link to="/signup" className="inline-block px-10 py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black text-lg uppercase tracking-widest shadow-xl transition-all hover:scale-105">
              Start Gratis Prøveperiode
            </Link>
          </div>
        </div>

      </div>

      <footer className="bg-slate-50 border-t border-slate-200 py-12 text-center text-slate-500 text-sm">
        <div className="flex justify-center gap-8 mb-4 font-bold">
          <Link to="/terms">Vilkår</Link>
          <Link to="/privacy">Personvern</Link>
          <Link to="/cookies">Cookies</Link>
        </div>
        <p>&copy; 2024 SalesWork AS.</p>
      </footer>
    </div>
  );
};

export default Features;